/**
 * 支付流程 Composable
 * 封装完整的支付流程（创建订单 → 获取二维码 → 轮询状态）
 */

import { ref, computed, onUnmounted, type Ref, type ComputedRef } from 'vue'
import type { PaymentService } from '../core/services/PaymentService'
import type { Package, PaymentQRCode } from '../types/payment'

/** 支付状态 */
export type PaymentStatus = 'idle' | 'creating' | 'paying' | 'success' | 'failed' | 'cancelled'

export interface UsePaymentFlowOptions {
  /** 支付成功回调 */
  onSuccess?: () => void | Promise<void>
  /** 支付失败回调 */
  onFailed?: (error?: Error) => void
  /** 支付取消回调 */
  onCancelled?: () => void
}

export interface UsePaymentFlowReturn {
  /** 套餐列表 */
  packages: Ref<Package[]>
  /** 当前选中的套餐 */
  selectedPackage: Ref<Package | null>
  /** 支付二维码信息 */
  paymentQRCode: Ref<PaymentQRCode | null>
  /** 支付状态 */
  status: Ref<PaymentStatus>
  /** 是否正在加载套餐 */
  isLoadingPackages: Ref<boolean>
  /** 是否正在支付 */
  isPaying: ComputedRef<boolean>
  /** 是否支付成功 */
  isSuccess: ComputedRef<boolean>
  /** 加载套餐列表 */
  loadPackages: () => Promise<boolean>
  /** 选择套餐 */
  selectPackage: (pkg: Package) => void
  /** 开始支付 */
  startPayment: (packageId?: number, paymentMethod?: string) => Promise<boolean>
  /** 取消支付 */
  cancelPayment: () => void
  /** 重置状态 */
  reset: () => void
}

/**
 * 创建支付流程 Composable
 * @param paymentService 支付服务实例
 */
export function createUsePaymentFlow(paymentService: PaymentService) {
  return function usePaymentFlow(options: UsePaymentFlowOptions = {}): UsePaymentFlowReturn {
    const { onSuccess, onFailed, onCancelled } = options

    // 状态
    const packages = ref<Package[]>([])
    const selectedPackage = ref<Package | null>(null)
    const paymentQRCode = ref<PaymentQRCode | null>(null)
    const status = ref<PaymentStatus>('idle')
    const isLoadingPackages = ref(false)

    // 计算属性
    const isPaying = computed(() =>
      status.value === 'creating' || status.value === 'paying'
    )
    const isSuccess = computed(() => status.value === 'success')

    // 加载套餐列表
    const loadPackages = async (): Promise<boolean> => {
      isLoadingPackages.value = true
      try {
        const result = await paymentService.getPackages()
        packages.value = result
        return true
      } catch (error) {
        console.error('[usePaymentFlow] 加载套餐失败:', error)
        return false
      } finally {
        isLoadingPackages.value = false
      }
    }

    // 选择套餐
    const selectPackage = (pkg: Package) => {
      selectedPackage.value = pkg
    }

    // 开始支付
    const startPayment = async (packageId?: number, paymentMethod = 'wechat'): Promise<boolean> => {
      const pkgId = packageId ?? selectedPackage.value?.id
      if (!pkgId) {
        console.error('[usePaymentFlow] 未选择套餐')
        return false
      }

      status.value = 'creating'

      try {
        // 创建订单并获取支付二维码
        const { order, qrCode } = await paymentService.createOrderAndGetQRCode({
          package_id: pkgId,
          payment_method: paymentMethod as 'wechat' | 'alipay' | 'miniprogram'
        })

        paymentQRCode.value = qrCode
        status.value = 'paying'

        // 开始轮询支付状态
        await paymentService.startPaymentPolling(
          order.order.id,
          // 支付成功
          async () => {
            status.value = 'success'
            paymentQRCode.value = null
            await onSuccess?.()
          },
          // 支付失败
          () => {
            status.value = 'failed'
            paymentQRCode.value = null
            onFailed?.()
          }
        )

        return true
      } catch (error) {
        console.error('[usePaymentFlow] 支付失败:', error)
        status.value = 'failed'
        onFailed?.(error as Error)
        return false
      }
    }

    // 取消支付
    const cancelPayment = () => {
      paymentService.stopPaymentPolling()
      status.value = 'cancelled'
      paymentQRCode.value = null
      onCancelled?.()
    }

    // 重置状态
    const reset = () => {
      paymentService.stopPaymentPolling()
      selectedPackage.value = null
      paymentQRCode.value = null
      status.value = 'idle'
    }

    // 组件卸载时清理
    onUnmounted(() => {
      paymentService.stopPaymentPolling()
    })

    return {
      packages,
      selectedPackage,
      paymentQRCode,
      status,
      isLoadingPackages,
      isPaying,
      isSuccess,
      loadPackages,
      selectPackage,
      startPayment,
      cancelPayment,
      reset
    }
  }
}
